import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { topTabs } from "../../utils/constants";
import { IoChevronDown } from "react-icons/io5";
import { useEmails } from "../../context/EmailsProvider";
import { useTeams } from "../../context/TeamsProvider";
import { pages } from "@microsoft/teams-js"; 
const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID

const Tabbar = ({ absolute = false }) => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftPos, setScrollLeftPos] = useState(0);

  // Drag-to-scroll handlers for desktop tabbar
  const handleMouseDown = (e) => {
    if (!tabsContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tabsContainerRef.current.offsetLeft);
    setScrollLeftPos(tabsContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !tabsContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; 
    tabsContainerRef.current.scrollLeft = scrollLeftPos - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Convert vertical wheel scroll to horizontal scroll
  const handleWheel = (e) => {
    if (!tabsContainerRef.current) return;
    e.preventDefault();
    tabsContainerRef.current.scrollLeft += e.deltaY;
  };
  const { isTeamsInitialized } = useTeams();
  const {
    newEmailCount,
    priorities,
  } = useEmails();

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  // calculating email notification badgeColor based on the highest priority 
  const highestPriority = priorities.length > 0 ? Math.max(...priorities) : null;
  const badgeColor = highestPriority === 0 || highestPriority === 1
    ? "bg-[#5652CF]"
    : highestPriority === 2 || highestPriority === 3
      ? "bg-yellow-500"
      : highestPriority >= 4
        ? "bg-red-600"
        : "bg-gray-500";

  const currentTab = topTabs.find(tab => tab.link === location.pathname);

  const handleTabClick = (tab) => {
    if (isMobile) {
      setIsExpanded(false);
    }
  };
  const handleTeamsTabSwitch = (tab) => {
    if (!isTeamsInitialized || !tab.entityId) return;

    const targetPageId = tab.entityId;

    if (pages.currentApp.isSupported()) {
      pages.currentApp.navigateTo({
        pageId: targetPageId,
      }).then(() => {
        // console.log(`Successfully navigated to pageId: ${targetPageId} using currentApp.navigateTo`);
      }).catch((error) => {
        console.error(`Error navigating to pageId: ${targetPageId} using currentApp.navigateTo:`, error);
      });
    } else {
      pages.navigateToApp({
        appId: TEAMS_APP_ID,
        pageId: targetPageId,
      }).then(() => {
        // console.log(`Successfully navigated to pageId: ${targetPageId} using navigateToApp`);
      }).catch((error) => {
        console.error(`Error navigating to pageId: ${targetPageId} using navigateToApp:`, error);
      });
    }
  };

  return (
    <div
      className={`${absolute ? "absolute top-2" : "fixed top-5"
        } left-1/2 transform -translate-x-1/2 z-[50]`}
      ref={dropdownRef}
    >
      {/* Mobile Tabbar */}
      {isMobile ? (
        <div className="relative">
          {/* Current Tab Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-2 p-2 pr-3 bg-3 rounded-full border br-1 transition-all duration-200"
          >
            <span className="relative flex items-center gap-1 bg-primary-gradient text-white px-2.5 py-1 rounded-full text-xs font-semibold">
              {currentTab && <currentTab.icon />}
              <span>{currentTab?.label || "Select"}</span>
              {/* Notification Badge */}
              {(currentTab?.label === "Communication") && newEmailCount > 0 && (
                <div
                  className={`absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-white text-xs font-bold rounded-full ${badgeColor}`}
                >
                  {newEmailCount}
                </div>
              )}
            </span>
            <IoChevronDown
              className={`text-s-text transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isExpanded && (
            <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-3 rounded-lg border br-1 shadow-lg min-w-[200px] py-2">
              {topTabs.map((tab, index) =>
                isTeamsInitialized && tab.entityId ? (
                  <button
                    key={index}
                    onClick={() => handleTeamsTabSwitch(tab)}
                    className={`relative w-full text-left flex items-center gap-2 px-4 py-2 mx-2 rounded-lg transition-all duration-200 ${location.pathname === tab.link
                      ? "bg-primary-gradient text-white font-semibold"
                      : "hover:bg-white hover:bg-opacity-10 text-s-text"
                      }`}
                  >
                    <tab.icon />
                    <span className="text-sm">{tab.label}</span>
                    {tab.label === "Communication" && newEmailCount > 0 && (
                      <div
                        className={`absolute right-2 flex items-center justify-center w-4 h-4 text-white text-xs font-bold rounded-full ${badgeColor}`}
                      >
                        {newEmailCount}
                      </div>
                    )}
                  </button>
                ) : (
                  <Link
                    to={tab.link}
                    key={index}
                    onClick={() => handleTabClick(tab)}
                    className="block"
                  >
                    <div
                      className={`relative flex items-center gap-2 px-4 py-2 mx-2 rounded-lg transition-all duration-200 ${location.pathname === tab.link
                        ? "bg-primary-gradient text-white font-semibold"
                        : "hover:bg-white hover:bg-opacity-10 text-s-text"
                        }`}
                    >
                      <tab.icon />
                      <span className="text-sm">{tab.label}</span>
                      {/* Notification Badge */}
                      {(tab.label === "Communication") && newEmailCount > 0 && (
                        <div
                          className={`absolute right-2 flex items-center justify-center w-4 h-4 text-white text-xs font-bold rounded-full ${badgeColor}`}
                        >
                          {newEmailCount}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
            </div>
          )}
        </div>
      ) : (
        /* Desktop Tabbar with drag-to-scroll */
        <div
          ref={tabsContainerRef}
          className={`inline-flex flex-nowrap p-1 gap-1 bg-3 rounded-full border br-1 max-w-[700px] overflow-x-auto hide-scrollbar ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
        >
          {topTabs.map((tab, index) => isTeamsInitialized && tab.entityId ? (
            <button
              key={index}
              onClick={() => handleTeamsTabSwitch(tab)}
              className={`relative flex items-center gap-1 min-h-5 px-2.5 py-1 rounded-full border-0 transition-all duration-200 flex-shrink-0 whitespace-nowrap ${location.pathname === tab.link
                ? "bg-primary-gradient text-white text-p-text font-semibold"
                : "bg-transparent hover:text-s-text text-s-text font-normal"
                }`}
              style={{ fontSize: "0.6rem" }}
            >
              <tab.icon />
              <span className="text-xs">{tab.label}</span>
              {tab.label === "Communication" && newEmailCount > 0 && (
                <div
                  className={`absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-white text-xs font-bold rounded-full ${badgeColor}`}
                >
                  {newEmailCount}
                </div>
              )}
            </button>
          ) : (
            <Link to={tab.link} key={index}>
              <span
                className={`relative flex items-center gap-1 disabled:cursor-not-allowed min-h-5 px-2.5 py-1 rounded-full border-0 transition-all duration-200 flex-shrink-0 whitespace-nowrap ${location.pathname === tab.link
                  ? "bg-primary-gradient text-white text-p-text font-semibold"
                  : "bg-transparent hover:text-s-text text-s-text font-normal"
                  }`}
                style={{ fontSize: "0.6rem" }}
              >
                {<tab.icon />}
                <span className="text-xs">{tab.label}</span>
                {/* Notification Badge */}
                {(tab.label === "Communication") && newEmailCount > 0 && (
                  <div
                    className={`absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-white text-xs font-bold rounded-full ${badgeColor}`}
                  >
                    {newEmailCount}
                  </div>
                )}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tabbar;